import { useState } from 'react';
import { gmailApi, propertiesApi, formatCurrency, MONTHS } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileText, Check, X, AlertCircle, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';

export const ReviewQueue = ({ candidates, properties, onImported, onDismissed }) => {
  const [items, setItems] = useState(
    candidates.map(c => ({
      ...c,
      selectedPropertyId: c.auto_property_id || '',
      selectedMonth: c.auto_month || '',
      selectedYear: c.auto_year ? String(c.auto_year) : '',
      status: 'pending', // pending, importing, imported, failed, dismissed
      errorMessage: null,
      reportLimitReached: false,
    }))
  );

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const checkReportLimit = async (item, propertyId, month, year) => {
    // This is a frontend check — backend enforces too
    // We'll just track it locally
    return false;
  };

  const handlePropertyChange = (idx, value) => {
    setItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, selectedPropertyId: value, reportLimitReached: false } : item
    ));
  };

  const handleMonthChange = (idx, value) => {
    setItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, selectedMonth: value, reportLimitReached: false } : item
    ));
  };

  const handleYearChange = (idx, value) => {
    setItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, selectedYear: value, reportLimitReached: false } : item
    ));
  };

  const handleImport = async (idx) => {
    const item = items[idx];
    setItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, status: 'importing', errorMessage: null } : it
    ));

    try {
      await gmailApi.importPdf({
        message_id: item.message_id,
        attachment_index: item.attachment_index,
        property_id: item.selectedPropertyId,
        month: item.selectedMonth,
        year: parseInt(item.selectedYear),
        pdf_url: item.pdf_url || null,
      });

      setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, status: 'imported' } : it
      ));
      toast.success(`Imported ${item.filename}`);
      onImported?.();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Import failed';
      const isLimitReached = detail.includes('3 reports');
      setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, status: 'failed', errorMessage: detail, reportLimitReached: isLimitReached } : it
      ));
      toast.error(detail);
    }
  };

  const handleDismiss = async (idx) => {
    const item = items[idx];
    try {
      await gmailApi.dismiss({
        message_id: item.message_id,
        attachment_index: item.attachment_index,
      });
      setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, status: 'dismissed' } : it
      ));
      onDismissed?.();
    } catch (error) {
      toast.error('Failed to dismiss');
    }
  };

  const pendingItems = items.filter(i => i.status === 'pending' || i.status === 'failed');
  const completedItems = items.filter(i => i.status === 'imported' || i.status === 'dismissed');

  return (
    <div className="space-y-4" data-testid="review-queue">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900 font-display">
          Review Queue ({pendingItems.length} pending)
        </h3>
        {completedItems.length > 0 && (
          <span className="text-xs text-slate-500">
            {completedItems.length} processed
          </span>
        )}
      </div>

      {items.map((item, idx) => {
        if (item.status === 'dismissed') return null;

        const canImport = item.selectedPropertyId && item.selectedMonth && item.selectedYear && !item.reportLimitReached;
        const isComplete = item.status === 'imported';

        return (
          <Card
            key={`${item.message_id}-${item.attachment_index}`}
            className={`border-slate-200 ${isComplete ? 'opacity-60' : ''}`}
            data-testid={`review-item-${item.message_id}`}
          >
            <CardContent className="p-4">
              <div className="flex flex-col gap-3">
                {/* Email metadata */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Mail className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate" data-testid="review-item-sender">
                        {item.sender_name}
                        <span className="text-slate-400 font-normal ml-1 text-xs">{item.sender_email}</span>
                      </p>
                      <p className="text-sm text-slate-600 truncate" data-testid="review-item-subject">{item.subject}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">{item.email_date}</span>
                        <Badge variant="outline" className="text-xs">
                          <FileText className="w-3 h-3 mr-1" />
                          {item.filename}
                          {item.pdf_url && <span className="ml-1 text-blue-500">(link)</span>}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {isComplete && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 flex-shrink-0">
                      <Check className="w-3 h-3 mr-1" /> Imported
                    </Badge>
                  )}
                </div>

                {/* Property & Month selectors + actions */}
                {!isComplete && (
                  <div className="flex flex-wrap items-end gap-3 pl-12">
                    {/* Property */}
                    <div className="flex-1 min-w-[180px]">
                      <label className="text-xs text-slate-500 mb-1 block">Property</label>
                      <Select
                        value={item.selectedPropertyId}
                        onValueChange={(v) => handlePropertyChange(idx, v)}
                        disabled={item.status === 'importing'}
                      >
                        <SelectTrigger className="h-9" data-testid={`review-property-${idx}`}>
                          <SelectValue placeholder="Select property..." />
                        </SelectTrigger>
                        <SelectContent>
                          {properties.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.address.split(',')[0].trim()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Month */}
                    <div className="w-28">
                      <label className="text-xs text-slate-500 mb-1 block">Month</label>
                      <Select
                        value={item.selectedMonth}
                        onValueChange={(v) => handleMonthChange(idx, v)}
                        disabled={item.status === 'importing'}
                      >
                        <SelectTrigger className="h-9" data-testid={`review-month-${idx}`}>
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTHS.map(m => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Year */}
                    <div className="w-24">
                      <label className="text-xs text-slate-500 mb-1 block">Year</label>
                      <Select
                        value={item.selectedYear}
                        onValueChange={(v) => handleYearChange(idx, v)}
                        disabled={item.status === 'importing'}
                      >
                        <SelectTrigger className="h-9" data-testid={`review-year-${idx}`}>
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent>
                          {years.map(y => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleImport(idx)}
                        disabled={!canImport || item.status === 'importing'}
                        className="bg-slate-900 hover:bg-slate-800 h-9"
                        data-testid={`review-import-${idx}`}
                      >
                        {item.status === 'importing' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Import'
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDismiss(idx)}
                        disabled={item.status === 'importing'}
                        className="h-9 text-slate-500 hover:text-red-600"
                        data-testid={`review-dismiss-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Error message */}
                {item.errorMessage && item.status === 'failed' && (
                  <div className="flex items-center gap-2 ml-12 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{item.errorMessage}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {pendingItems.length === 0 && completedItems.length > 0 && (
        <p className="text-sm text-slate-500 text-center py-4">
          All items have been processed.
        </p>
      )}
    </div>
  );
};

export default ReviewQueue;
